import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PageTitle } from '@/components/PageTitle';
import { formatDateTime } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';
import { saveFile } from '@/lib/download';
import { errorText } from '@/lib/errors';
import { useToast } from '@/components/ui/Toaster';
import { Link } from '@/lib/router-compat';
import { useCan } from '@/lib/session';

export const Route = createFileRoute('/_app/leads/')({
  validateSearch: z.object({ q: z.string().optional(), test: z.boolean().optional(), campaign: z.number().int().optional() }),
  beforeLoad: ({ context }) => requirePermission(context.me, 'leads.view'),
  component: LeadsPage
});

function LeadsPage() {
  const { t } = useTranslation();
  const can = useCan();
  const pii = can('leads.view_pii');
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [q, setQ] = useState(search.q ?? '');

  // Debounced search → URL (shareable, back button works).
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = q.trim().length >= 2 ? q.trim() : undefined;
      if (next !== search.q) void navigate({ search: (s) => ({ ...s, q: next }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [q, search.q, navigate]);

  const leads = useInfiniteQuery(
    orpc.leads.list.infiniteOptions({
      input: (cursor: string | undefined) => ({
        limit: 50,
        includeTest: search.test ?? false,
        ...(search.q && { search: search.q }),
        ...(search.campaign && { campaignId: search.campaign }),
        ...(cursor && { cursor })
      }),
      initialPageParam: undefined,
      getNextPageParam: (last) => last.nextCursor ?? undefined
    })
  );
  const items = leads.data?.pages.flatMap((p) => p.items) ?? [];
  const toast = useToast();
  const exportCsv = useMutation({
    mutationFn: () =>
      client.leads.export({
        includeTest: search.test ?? false,
        ...(search.q && { search: search.q }),
        ...(search.campaign && { campaignId: search.campaign })
      }),
    onSuccess: (file) => saveFile(file, 'holymoly-leads.csv'),
    onError: (e) => toast('error', errorText(t, e))
  });

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('leads.title')}
        items={[{ label: t('linkedin.section') }, { label: t('leads.title'), active: true }]}
        actions={
          can('leads.export') && (
            <button type="button" className="btn btn-sm btn-outline border-base-300" title={t('leads.exportHint')} disabled={exportCsv.isPending} onClick={() => exportCsv.mutate()}>
              <span className="iconify lucide--download size-4" />
              {t('leads.exportCsv')}
            </button>
          )
        }
      />
      {!pii && (
        <div className="alert alert-info alert-soft text-sm">
          <span className="iconify lucide--eye-off size-4" />
          {t('leads.masked')}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <label className="input input-sm w-80">
          <span className="iconify lucide--search size-4 opacity-60" />
          <input
            type="search"
            placeholder={t('leads.search', { email: pii ? t('leads.searchEmail') : '' })}
            aria-label={t('leads.search', { email: pii ? t('leads.searchEmail') : '' })}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="toggle toggle-sm"
            checked={search.test ?? false}
            onChange={(e) => void navigate({ search: (s) => ({ ...s, test: e.target.checked || undefined }), replace: true })}
          />
          {t('leads.includeTest')}
        </label>
      </div>
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('leads.submitted')}</th>
                  <th>{t('leads.name')}</th>
                  <th>{t('leads.company')}</th>
                  <th>{t('leads.email')}</th>
                  <th>{t('leads.campaign')}</th>
                </tr>
              </thead>
              <tbody>
                {leads.isSuccess && items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-base-content/60">
                      {t('leads.none')}
                    </td>
                  </tr>
                )}
                {items.map((l) => (
                  <tr key={l.id} className="hover:bg-base-200/50">
                    <td className="text-sm whitespace-nowrap">{formatDateTime(l.submittedAt)}</td>
                    <td>
                      <Link to={`/leads/${encodeURIComponent(l.id)}`} className="link link-hover font-medium">
                        {[l.firstName, l.lastName].filter(Boolean).join(' ') || '–'}
                      </Link>
                      {l.testLead && <span className="badge badge-ghost badge-xs ms-2">{t('leads.test')}</span>}
                      {l.withdrawn && <span className="badge badge-warning badge-soft badge-xs ms-2">{t('leads.withdrawn')}</span>}
                    </td>
                    <td className="text-sm">{l.company ?? '–'}</td>
                    <td className="text-sm">{l.email ?? '–'}</td>
                    <td className="text-sm">{l.campaignName ?? l.formName ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {leads.hasNextPage && (
        <div className="text-center">
          <button type="button" className="btn btn-sm" onClick={() => void leads.fetchNextPage()} disabled={leads.isFetchingNextPage}>
            {t('admin.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}
